import {forwardRef, type KeyboardEvent} from 'react';
import {ChevronDown, ChevronUp, FileText, Search, X} from 'lucide-react';
import {useIntl} from 'react-intl';
import {insightsMessages as m} from '../insightsMessages';
import {focusRing, iconButton, lucide, secondaryButton} from '../insightsUi';
import type {TranscriptSearch} from './useTranscriptSearch';

interface TranscriptSearchBarProps {
    search: TranscriptSearch;
    onExport: () => void;
}

export const TranscriptSearchBar = forwardRef<HTMLInputElement, TranscriptSearchBarProps>(({search, onExport}, ref) => {
    const intl = useIntl();
    const total = search.matches.length;
    const hasQuery = search.query.trim().length > 0;

    const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            if (event.shiftKey) search.previous();
            else search.next();
        }
    };

    return (
        <div className="flex items-center gap-2">
            <label className="flex h-9 min-w-0 flex-1 items-center gap-2 rounded-control bg-surface-muted px-3 hover:bg-line focus-within:ring-2 focus-within:ring-brand-focus">
                <Search size={16} aria-hidden="true" className={`${lucide} !text-ink-muted`}/>
                <input
                    ref={ref}
                    type="search"
                    value={search.query}
                    onChange={(event) => search.setQuery(event.target.value)}
                    onKeyDown={onKeyDown}
                    placeholder={intl.formatMessage(m.searchPlaceholder)}
                    aria-label={intl.formatMessage(m.searchLabel)}
                    data-insights-search=""
                    className="min-w-0 flex-1 border-0 bg-transparent p-0 text-callout text-ink outline-none placeholder:text-ink-muted focus:ring-0 [&::-webkit-search-cancel-button]:hidden"
                />
                {hasQuery && (
                    <>
                        <span aria-live="polite" className="shrink-0 text-footnote tabular-nums text-ink-muted">
                            {total > 0 ? intl.formatMessage(m.searchCount, {current: search.current + 1, total}) : intl.formatMessage(m.searchNone)}
                        </span>
                        <button
                            type="button"
                            aria-label={intl.formatMessage(m.searchClear)}
                            title={intl.formatMessage(m.searchClear)}
                            onClick={search.clear}
                            className={`inline-flex size-5 shrink-0 cursor-pointer items-center justify-center rounded-full border-0 bg-fill-subtle p-0 text-ink-secondary ${focusRing}`}
                        >
                            <X size={12} aria-hidden="true" className={lucide}/>
                        </button>
                    </>
                )}
            </label>
            {hasQuery && (
                <>
                    <button type="button" aria-label={intl.formatMessage(m.searchPrevious)} disabled={total === 0} onClick={search.previous} className={iconButton}>
                        <ChevronUp size={18} aria-hidden="true" className={lucide}/>
                    </button>
                    <button type="button" aria-label={intl.formatMessage(m.searchNext)} disabled={total === 0} onClick={search.next} className={iconButton}>
                        <ChevronDown size={18} aria-hidden="true" className={lucide}/>
                    </button>
                </>
            )}
            <button type="button" onClick={onExport} className={`${secondaryButton} h-9`}>
                <FileText size={16} aria-hidden="true" className={lucide}/>
                {intl.formatMessage(m.exportTranscript)}
            </button>
        </div>
    );
});

TranscriptSearchBar.displayName = 'TranscriptSearchBar';
